import * as Sentry from '@sentry/react'
import {
  Centrifuge,
  type ConnectedContext,
  type ConnectingContext,
  type DisconnectedContext,
  type ErrorContext,
  type ServerPublicationContext,
  type ServerSubscribedContext,
  type ServerSubscribingContext,
  type ServerUnsubscribedContext,
} from 'centrifuge'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { AuthModel } from '~/entities/auth'
import { RealtimeService } from '~/shared/api/services/realtime'
import {
  RealtimeConnectionState,
  RealtimeDebugEvent,
  RealtimeDebugMessage,
  RealtimeEventPayload,
} from '~/shared/api/services/realtime/types'
import {
  getErrorMessage,
  shouldCaptureSentryException,
} from '~/entities/realtime/lib/sentry-error'
import {
  isDemoEnvironment,
  isProductionMode,
  isStagingEnvironment,
} from '~/shared/lib/environment'

const STAGING_REALTIME_WS_URL =
  'wss://ws-staging.mateline.ru/connection/websocket'
const PRODUCTION_REALTIME_WS_URL = 'wss://ws.mateline.ru/connection/websocket'

type RealtimeEventHandler = (payload: RealtimeEventPayload) => void
const REALTIME_DEBUG_MESSAGES_LIMIT = 100
const REALTIME_DEBUG_EVENTS_LIMIT = 50

type SubscriptionStatus = {
  state: 'subscribed' | 'subscribing' | 'unsubscribed'
  code?: number
  reason?: string
  recovered?: boolean
  recoverable?: boolean
  lastUpdatedAt: string
}

const getRealtimeWebsocketUrl = () => {
  if (import.meta.env.VITE_REALTIME_WS_URL) {
    return import.meta.env.VITE_REALTIME_WS_URL
  }

  if (isDemoEnvironment) {
    return ''
  }

  if (isStagingEnvironment) {
    return STAGING_REALTIME_WS_URL
  }

  if (isProductionMode) {
    return PRODUCTION_REALTIME_WS_URL
  }

  return STAGING_REALTIME_WS_URL
}

export class RealtimeModel {
  connectionState: RealtimeConnectionState = 'disconnected'

  websocketUrl = getRealtimeWebsocketUrl()

  lastToken = ''

  lastChannels: string[] = []

  lastEvent: RealtimeEventPayload | null = null

  messages: RealtimeDebugMessage[] = []

  debugEvents: RealtimeDebugEvent[] = []

  lastError = ''

  lastErrorType = ''

  reconnectAttempts = 0

  lastConnecting: { code?: number; reason?: string; at?: string } = {}

  lastConnected: { transport?: string; client?: string; at?: string } = {}

  lastDisconnected: { code?: number; reason?: string; at?: string } = {}

  subscriptionStatuses: Record<string, SubscriptionStatus> = {}

  private client: Centrifuge | null = null

  private initializationId = 0

  private readonly eventHandlers: Record<string, RealtimeEventHandler>

  constructor(private readonly authStore: AuthModel) {
    makeAutoObservable(this, {}, { autoBind: true })

    this.eventHandlers = {
      'system.notification': this.handleSystemNotification,
      'maintenance.started': this.handleMaintenanceStarted,
      'maintenance.finished': this.handleMaintenanceFinished,
      'order.status_counts.updated': this.handleOrdersStatusCountsUpdated,
      'shipping.orders.updated': this.handleShippingOrdersUpdated,
      'ticket.message.created': this.handleTicketNotificationCreated,
      'ticket.notification.created': this.handleTicketNotificationCreated,
      'team.notification': this.handleTeamNotification,
      'user.notification': this.handleUserNotification,
    }

    reaction(
      () => this.authStore.sessionToken,
      (sessionToken, previousToken) => {
        if (!sessionToken) {
          this.disconnect({ clearMetadata: true })
          return
        }

        const shouldReinitialize =
          !this.client || !previousToken || previousToken !== sessionToken

        if (shouldReinitialize) {
          this.initialize(sessionToken)
        }
      },
      { fireImmediately: true },
    )
  }

  get isConnected() {
    return this.connectionState === 'connected'
  }

  getMessagesByChannel = (channel: string) =>
    this.messages.filter((message) => message.channel === channel)

  initialize = async (sessionToken: string) => {
    if (isDemoEnvironment && !import.meta.env.VITE_REALTIME_WS_URL) {
      return
    }

    const nextInitializationId = this.initializationId + 1
    this.initializationId = nextInitializationId

    runInAction(() => {
      this.connectionState = 'connecting'
      this.lastError = ''
      this.lastErrorType = ''
    })

    try {
      const { token, channels } =
        await RealtimeService.getConnectionToken(sessionToken)

      if (nextInitializationId !== this.initializationId) {
        return
      }

      this.disconnect()

      runInAction(() => {
        this.lastToken = token
        this.lastChannels = channels
      })

      const client = new Centrifuge(getRealtimeWebsocketUrl(), {
        token,
      })

      client.on('connecting', this.handleConnecting)
      client.on('connected', this.handleConnected)
      client.on('disconnected', this.handleDisconnected)
      client.on('error', this.handleError)
      client.on('subscribing', this.handleSubscribing)
      client.on('subscribed', this.handleSubscribed)
      client.on('unsubscribed', this.handleUnsubscribed)
      client.on('publication', this.handlePublication)

      this.client = client
      client.connect()
    } catch (error) {
      if (nextInitializationId !== this.initializationId) {
        return
      }

      this.captureError(error, 'Failed to initialize realtime connection')
      runInAction(() => {
        this.connectionState = 'error'
      })
    }
  }

  disconnect = ({
    clearMetadata = false,
  }: { clearMetadata?: boolean } = {}) => {
    if (this.client) {
      this.client.removeAllListeners()
      this.client.disconnect()
      this.client = null
    }

    runInAction(() => {
      this.connectionState = 'disconnected'

      if (clearMetadata) {
        this.lastToken = ''
        this.lastChannels = []
        this.lastEvent = null
        this.messages = []
        this.debugEvents = []
        this.lastError = ''
        this.lastErrorType = ''
        this.reconnectAttempts = 0
        this.lastConnecting = {}
        this.lastConnected = {}
        this.lastDisconnected = {}
        this.subscriptionStatuses = {}
      }
    })
  }

  private handleConnecting = (ctx: ConnectingContext) => {
    Sentry.addBreadcrumb({
      category: 'realtime',
      level: 'info',
      message: `Realtime connecting: ${ctx.code}`,
    })

    runInAction(() => {
      this.connectionState = 'connecting'
      this.lastError = ''
      this.lastErrorType = ''
      this.reconnectAttempts += 1
      this.lastConnecting = {
        code: ctx.code,
        reason: ctx.reason,
        at: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'connection',
      type: 'connecting',
      code: ctx.code,
      reason: ctx.reason,
    })
  }

  private handleConnected = (ctx: ConnectedContext) => {
    Sentry.addBreadcrumb({
      category: 'realtime',
      level: 'info',
      message: `Realtime connected via ${ctx.transport}`,
    })

    runInAction(() => {
      this.connectionState = 'connected'
      this.lastError = ''
      this.lastErrorType = ''
      this.reconnectAttempts = 0
      this.lastConnected = {
        transport: ctx.transport,
        client: ctx.client,
        at: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'connection',
      type: 'connected',
      transport: ctx.transport,
      message: ctx.client,
    })
  }

  private handleDisconnected = (ctx: DisconnectedContext) => {
    runInAction(() => {
      this.connectionState = ctx.code === 0 ? 'disconnected' : 'error'
      this.lastError = ctx.reason || this.lastError
      this.lastDisconnected = {
        code: ctx.code,
        reason: ctx.reason,
        at: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'connection',
      type: 'disconnected',
      code: ctx.code,
      reason: ctx.reason,
    })
  }

  private handleError = (ctx: ErrorContext) => {
    this.captureError(ctx.error, 'Realtime transport error')
    runInAction(() => {
      this.connectionState = 'error'
      this.lastErrorType = ctx.type
    })

    this.pushDebugEvent({
      scope: 'connection',
      type: 'error',
      transport: ctx.transport,
      errorType: ctx.type,
      code: ctx.error.code,
      message: ctx.error.message,
    })
  }

  private handleSubscribing = (ctx: ServerSubscribingContext) => {
    runInAction(() => {
      this.subscriptionStatuses[ctx.channel] = {
        state: 'subscribing',
        lastUpdatedAt: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'subscription',
      type: 'subscribing',
      channel: ctx.channel,
    })
  }

  private handleSubscribed = (ctx: ServerSubscribedContext) => {
    runInAction(() => {
      this.subscriptionStatuses[ctx.channel] = {
        state: 'subscribed',
        recovered: ctx.recovered,
        recoverable: ctx.recoverable,
        lastUpdatedAt: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'subscription',
      type: 'subscribed',
      channel: ctx.channel,
      message: `recoverable=${String(ctx.recoverable)}, recovered=${String(
        ctx.recovered,
      )}`,
    })
  }

  private handleUnsubscribed = (ctx: ServerUnsubscribedContext) => {
    runInAction(() => {
      this.subscriptionStatuses[ctx.channel] = {
        state: 'unsubscribed',
        lastUpdatedAt: new Date().toISOString(),
      }
    })

    this.pushDebugEvent({
      scope: 'subscription',
      type: 'unsubscribed',
      channel: ctx.channel,
    })
  }

  private handlePublication = (ctx: ServerPublicationContext) => {
    const { channel } = ctx
    const payload = (ctx.data || {}) as RealtimeEventPayload

    runInAction(() => {
      this.lastEvent = payload
      this.messages = [
        {
          id: `${channel}-${new Date().toISOString()}-${Math.random()
            .toString(36)
            .slice(2)}`,
          channel,
          payload,
          receivedAt: new Date().toISOString(),
        },
        ...this.messages,
      ].slice(0, REALTIME_DEBUG_MESSAGES_LIMIT)
    })

    const handler = payload.event
      ? this.eventHandlers[payload.event]
      : undefined

    if (handler) {
      handler(payload)
      return
    }

    if (!payload.event) {
      Sentry.addBreadcrumb({
        category: 'realtime',
        level: 'info',
        message: `Realtime payload without event received from ${channel}`,
      })
      return
    }

    Sentry.addBreadcrumb({
      category: 'realtime',
      level: 'info',
      message: `Unhandled realtime event: ${payload.event}`,
    })
  }

  private handleSystemNotification = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleMaintenanceStarted = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleMaintenanceFinished = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleOrdersStatusCountsUpdated = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleShippingOrdersUpdated = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleTicketNotificationCreated = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleTeamNotification = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private handleUserNotification = (payload: RealtimeEventPayload) => {
    this.lastEvent = payload
  }

  private captureError = (error: unknown, fallbackMessage: string) => {
    const message = getErrorMessage(error, fallbackMessage)

    runInAction(() => {
      this.lastError = message
    })

    if (!shouldCaptureSentryException(error)) {
      return
    }

    const exception = error instanceof Error ? error : new Error(message)

    Sentry.captureException(exception, {
      tags: {
        module: 'realtime',
      },
      extra: {
        connectionState: this.connectionState,
        channels: this.lastChannels,
        ...(typeof error === 'object' &&
        error !== null &&
        !(error instanceof Error)
          ? { centrifugeError: error }
          : {}),
      },
    })
  }

  private pushDebugEvent = (
    event: Omit<RealtimeDebugEvent, 'id' | 'createdAt'>,
  ) => {
    runInAction(() => {
      this.debugEvents = [
        {
          id: `${event.scope}-${event.type}-${Date.now()}-${Math.random()
            .toString(36)
            .slice(2)}`,
          createdAt: new Date().toISOString(),
          ...event,
        },
        ...this.debugEvents,
      ].slice(0, REALTIME_DEBUG_EVENTS_LIMIT)
    })
  }
}
